import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import { AuthProvider, useAuth, hasAdminSession, isStaff } from "@/context/AuthContext";
import ComingSoon from "@/pages/ComingSoon";
import { LangProvider } from "@/lib/i18n";
import CustomerShell from "@/components/CustomerShell";
import AdminShell from "@/components/AdminShell";
import { Loader } from "@/components/common";
import Landing from "@/pages/Landing";
import SearchPage from "@/pages/Search";
import ProviderPage from "@/pages/ProviderPage";
import BookingWizard from "@/pages/BookingWizard";
import AuthPage from "@/pages/AuthPage";
import ResetPassword from "@/pages/ResetPassword";
import Backoffice from "@/pages/Backoffice";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import Pricing from "@/pages/Pricing";
import BecomeProvider from "@/pages/BecomeProvider";
import Bookings from "@/pages/Bookings";
import BookingDetail from "@/pages/BookingDetail";
import Account from "@/pages/Account";
import ProviderHome from "@/pages/provider/ProviderHome";
import { Dashboard, Providers, Bookings as AdminBookings, Complaints, Insurance, Tickets, Reviews } from "@/pages/admin/AdminPages";
import { Finance, Config, Audit } from "@/pages/admin/AdminPages2";
import { Users, Staff, AdminInbox } from "@/pages/admin/AdminPages3";
import { Content, Blog as AdminBlog, Legal, WebsiteStatus } from "@/pages/admin/AdminPages4";
import { InsuranceReviews } from "@/pages/admin/AdminInsuranceReviews";
import { Compliance } from "@/pages/admin/AdminCompliance";
import LegalPage from "@/pages/LegalPage";
import WithdrawalPage from "@/pages/WithdrawalPage";
import { BlogList, BlogPost } from "@/pages/Blog";

function Protected({ children, check }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to={`/auth?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (check && !check(user)) return <Navigate to="/" replace />;
  return children;
}

function AdminGuard({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (!hasAdminSession(user)) return <Navigate to="/backoffice" replace />;
  return children;
}

const COMING_SOON_ALLOW = ["/backoffice", "/admin", "/auth", "/reset-password"];

function SiteGate({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  const [status, setStatus] = useState(undefined);
  useEffect(() => {
    const pv = new URLSearchParams(window.location.search).get("preview");
    if (pv) localStorage.setItem("washly_preview", pv);
    const token = localStorage.getItem("washly_preview");
    api.get(`/site-status${token ? `?preview=${encodeURIComponent(token)}` : ""}`)
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ coming_soon: false }));
  }, []);
  if (status === undefined || user === null) return <Loader />;
  if (!status.coming_soon) return children;
  const allowed = COMING_SOON_ALLOW.some((p) => loc.pathname === p || loc.pathname.startsWith(p + "/"));
  if (allowed || status.bypass || isStaff(user)) return children;
  return <ComingSoon title={status.title} message={status.message} />;
}

export default function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <SiteGate>
          <Routes>
            <Route element={<CustomerShell />}>
              <Route path="/" element={<Landing />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/p/:id" element={<ProviderPage />} />
              <Route path="/book/:id" element={<BookingWizard />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/preise" element={<Pricing />} />
              <Route path="/waschheld" element={<BecomeProvider />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/agb" element={<LegalPage slug="agb" />} />
              <Route path="/datenschutz" element={<LegalPage slug="datenschutz" />} />
              <Route path="/impressum" element={<LegalPage slug="impressum" />} />
              <Route path="/widerruf" element={<LegalPage slug="widerruf" />} />
              <Route path="/widerruf-erklaeren" element={<WithdrawalPage />} />
              <Route path="/bookings" element={<Protected><Bookings /></Protected>} />
              <Route path="/bookings/:id" element={<Protected><BookingDetail /></Protected>} />
              <Route path="/account" element={<Protected><Account /></Protected>} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/provider" element={<Protected check={(u) => u.role === "provider" || u.has_provider}><ProviderHome /></Protected>} />
            </Route>
            <Route path="/backoffice" element={<Backoffice />} />
            <Route path="/admin" element={<AdminGuard><AdminShell /></AdminGuard>}>
              <Route index element={<Dashboard />} />
              <Route path="providers" element={<Providers />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="users" element={<Users />} />
              <Route path="inbox" element={<AdminInbox />} />
              <Route path="complaints" element={<Complaints />} />
              <Route path="insurance" element={<Insurance />} />
              <Route path="insurance-reviews" element={<InsuranceReviews />} />
              <Route path="tickets" element={<Tickets />} />
              <Route path="reviews" element={<Reviews />} />
              <Route path="finance" element={<Finance />} />
              <Route path="config" element={<Config />} />
              <Route path="content" element={<Content />} />
              <Route path="blog" element={<AdminBlog />} />
              <Route path="legal" element={<Legal />} />              <Route path="compliance" element={<Compliance />} />
              <Route path="website-status" element={<WebsiteStatus />} />
              <Route path="staff" element={<Staff />} />
              <Route path="audit" element={<Audit />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </SiteGate>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </LangProvider>
  );
}
